import { useEffect, useMemo, useRef, useState } from "react";

import {
  useGetDemoAccessAssignmentsQuery,
  useGrantDemoAccessMutation,
  useRevokeDemoAccessMutation,
} from "@/store/api/baseApi";
import { getDemoCompanyEntryUrl } from "@/utils/canonicalUrls";
import {
  getErrorMessage,
  getPublicErrorDetails,
  toError,
} from "@/utils/errorHandling";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  VNextButton,
  VNextCard,
  VNextKeyValueList,
  VNextNotice,
  VNextStat,
} from "@/vnext/primitives";
import { DemoAccessAssignmentsCard } from "@/vnext/routes/settings/demo-access/DemoAccessAssignmentsCard";
import { DemoAccessDialogs } from "@/vnext/routes/settings/demo-access/DemoAccessDialogs";
import {
  buildDemoAccessGrantPayload,
  buildDemoAccessSummary,
  type DemoAccessIdentifierType,
  type DemoAccessRevokeTarget,
  filterDemoAccessAssignments,
  sortDemoAccessAssignments,
} from "@/vnext/routes/settings/demo-access/demoAccessRouteModel";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";
import { formatVNextDateTime } from "@/vnext/shared/formatters";

import "@/vnext/routes/settings/demo-access/demoAccessMake.css";

const logger =
  FrontendLoggerFactory.getLogger().withComponent("DemoAccessRoute");

const buildCorrelationSuffix = (correlationId: string | null): string =>
  correlationId ? ` (Ref: ${correlationId})` : "";

const buildDemoAccessGrantErrorMessage = (args: {
  error: unknown;
  identifierType: DemoAccessIdentifierType;
}) => {
  const details = getPublicErrorDetails(
    args.error,
    "Failed to grant demo access.",
  );

  if (details.errorCode === "NOT_FOUND") {
    const identifierLabel =
      args.identifierType === "firebaseId" ? "Firebase UID" : "email address";

    return `No user matched that ${identifierLabel}. Check it and try again${buildCorrelationSuffix(details.correlationId)}.`;
  }

  return getErrorMessage(args.error, "Failed to grant demo access.");
};

const buildDemoAccessRevokeErrorMessage = (error: unknown) => {
  const details = getPublicErrorDetails(error, "Failed to revoke demo access.");

  if (details.errorCode === "NOT_FOUND") {
    return `That user no longer exists. Refresh assignments and try again${buildCorrelationSuffix(details.correlationId)}.`;
  }

  return getErrorMessage(error, "Failed to revoke demo access.");
};

const isHandledDemoAccessLookupMiss = (errorCode: string | null): boolean =>
  errorCode === "NOT_FOUND";

export function DemoAccessRoute() {
  const pageStartedAtRef = useRef<number | null>(null);
  const readyLoggedRef = useRef(false);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [identifierType, setIdentifierType] =
    useState<DemoAccessIdentifierType>("email");
  const [userIdentifier, setUserIdentifier] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [showDisabledAssignments, setShowDisabledAssignments] = useState(false);
  const [revokeTarget, setRevokeTarget] =
    useState<DemoAccessRevokeTarget | null>(null);

  const { data, error, isFetching, isLoading, refetch } =
    useGetDemoAccessAssignmentsQuery();
  const [grantDemoAccess, { isLoading: isGranting }] =
    useGrantDemoAccessMutation();
  const [revokeDemoAccess, { isLoading: isRevoking }] =
    useRevokeDemoAccessMutation();

  const assignments = useMemo(
    () => sortDemoAccessAssignments(data ?? []),
    [data],
  );
  const summary = useMemo(
    () => buildDemoAccessSummary(assignments),
    [assignments],
  );
  const visibleAssignments = useMemo(
    () =>
      filterDemoAccessAssignments(assignments, {
        includeDisabledAssignments: showDisabledAssignments,
      }),
    [assignments, showDisabledAssignments],
  );
  const assignmentErrorMessage = useMemo(
    () =>
      error
        ? getErrorMessage(error, "Failed to load demo access assignments.")
        : null,
    [error],
  );

  useEffect(() => {
    logger.debug("DEMO_ACCESS_ROUTE_ENTER", {
      component: "DemoAccessRoute",
      feature: "ADMIN",
      operation: "DEMO_ACCESS_ROUTE_ENTER",
    });
    pageStartedAtRef.current = Date.now();
    readyLoggedRef.current = false;
  }, []);

  useEffect(() => {
    if (readyLoggedRef.current || isLoading || isFetching || error) {
      return;
    }

    readyLoggedRef.current = true;
    logger.debug("DEMO_ACCESS_ROUTE_READY", {
      assignmentCount: assignments.length,
      component: "DemoAccessRoute",
      enabledAssignmentCount: summary.activeAssignments,
      feature: "ADMIN",
      operation: "DEMO_ACCESS_ROUTE_READY",
      pageLoadDurationMs:
        pageStartedAtRef.current === null
          ? null
          : Date.now() - pageStartedAtRef.current,
    });
  }, [
    assignments.length,
    error,
    isFetching,
    isLoading,
    summary.activeAssignments,
  ]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const details = getPublicErrorDetails(
      error,
      "Failed to load demo access assignments.",
    );

    logger.error(
      "DEMO_ACCESS_ROUTE_FAIL",
      toError(error) ?? new Error("demo_access_route_failed"),
      {
        component: "DemoAccessRoute",
        correlationId: details.correlationId ?? undefined,
        errorCode: details.errorCode,
        feature: "ADMIN",
        operation: "DEMO_ACCESS_ROUTE_FAIL",
      },
    );
  }, [error]);

  const resetMessages = () => {
    setActionError(null);
    setActionSuccess(null);
  };

  const handleOpenGrantDialog = () => {
    logger.debug("DEMO_ACCESS_GRANT_DIALOG_OPEN", {
      component: "DemoAccessRoute",
      feature: "ADMIN",
      operation: "DEMO_ACCESS_GRANT_DIALOG_OPEN",
    });
    setGrantDialogOpen(true);
  };

  const handleCloseGrantDialog = () => {
    logger.debug("DEMO_ACCESS_GRANT_DIALOG_CLOSE", {
      component: "DemoAccessRoute",
      feature: "ADMIN",
      operation: "DEMO_ACCESS_GRANT_DIALOG_CLOSE",
    });
    setGrantDialogOpen(false);
    setUserIdentifier("");
    setIdentifierType("email");
  };

  const handleOpenRevokeDialog = (target: DemoAccessRevokeTarget) => {
    logger.debug("DEMO_ACCESS_REVOKE_DIALOG_OPEN", {
      component: "DemoAccessRoute",
      feature: "ADMIN",
      operation: "DEMO_ACCESS_REVOKE_DIALOG_OPEN",
      targetUserId: target.firebaseId,
    });
    setRevokeTarget(target);
  };

  const handleCloseRevokeDialog = () => {
    logger.debug("DEMO_ACCESS_REVOKE_DIALOG_CLOSE", {
      component: "DemoAccessRoute",
      feature: "ADMIN",
      operation: "DEMO_ACCESS_REVOKE_DIALOG_CLOSE",
      targetUserId: revokeTarget?.firebaseId,
    });
    setRevokeTarget(null);
  };

  const handleRefresh = async () => {
    logger.debug("DEMO_ACCESS_ROUTE_REFRESH", {
      component: "DemoAccessRoute",
      feature: "ADMIN",
      operation: "DEMO_ACCESS_ROUTE_REFRESH",
    });
    await refetch();
  };

  const handleGrantAccess = async () => {
    const payload = buildDemoAccessGrantPayload({
      identifierType,
      userIdentifier,
    });

    if (payload === null) {
      setActionError("Identifier is required.");
      logger.warn("DEMO_ACCESS_GRANT_VALIDATION_FAIL", {
        component: "DemoAccessRoute",
        feature: "ADMIN",
        operation: "DEMO_ACCESS_GRANT_VALIDATION_FAIL",
      });
      return;
    }

    resetMessages();
    logger.debug("DEMO_ACCESS_GRANT_START", {
      component: "DemoAccessRoute",
      feature: "ADMIN",
      identifierType,
      operation: "DEMO_ACCESS_GRANT_START",
    });

    try {
      await grantDemoAccess(payload).unwrap();
      logger.debug("DEMO_ACCESS_GRANT_SUCCESS", {
        component: "DemoAccessRoute",
        feature: "ADMIN",
        identifierType,
        operation: "DEMO_ACCESS_GRANT_SUCCESS",
      });
      setActionSuccess("Demo access granted successfully.");
      handleCloseGrantDialog();
    } catch (grantError) {
      const details = getPublicErrorDetails(
        grantError,
        "Failed to grant demo access.",
      );
      const failureMeta = {
        component: "DemoAccessRoute",
        correlationId: details.correlationId ?? undefined,
        errorCode: details.errorCode,
        feature: "ADMIN",
        identifierType,
      } as const;

      if (isHandledDemoAccessLookupMiss(details.errorCode)) {
        logger.debug("DEMO_ACCESS_GRANT_HANDLED_NOT_FOUND", {
          ...failureMeta,
          operation: "DEMO_ACCESS_GRANT_HANDLED_NOT_FOUND",
        });
      } else {
        logger.error(
          "DEMO_ACCESS_GRANT_FAIL",
          toError(grantError) ?? new Error("demo_access_grant_failed"),
          {
            ...failureMeta,
            operation: "DEMO_ACCESS_GRANT_FAIL",
          },
        );
      }
      setActionError(
        buildDemoAccessGrantErrorMessage({
          error: grantError,
          identifierType,
        }),
      );
    }
  };

  const handleConfirmRevoke = async () => {
    if (revokeTarget === null) {
      return;
    }

    resetMessages();
    logger.debug("DEMO_ACCESS_REVOKE_START", {
      component: "DemoAccessRoute",
      feature: "ADMIN",
      operation: "DEMO_ACCESS_REVOKE_START",
      targetUserId: revokeTarget.firebaseId,
    });

    try {
      await revokeDemoAccess({
        firebaseId: revokeTarget.firebaseId,
      }).unwrap();
      logger.debug("DEMO_ACCESS_REVOKE_SUCCESS", {
        component: "DemoAccessRoute",
        feature: "ADMIN",
        operation: "DEMO_ACCESS_REVOKE_SUCCESS",
        targetUserId: revokeTarget.firebaseId,
      });
      setActionSuccess(`Demo access revoked for ${revokeTarget.displayName}.`);
      handleCloseRevokeDialog();
    } catch (revokeError) {
      const details = getPublicErrorDetails(
        revokeError,
        "Failed to revoke demo access.",
      );
      const failureMeta = {
        component: "DemoAccessRoute",
        correlationId: details.correlationId ?? undefined,
        errorCode: details.errorCode,
        feature: "ADMIN",
        targetUserId: revokeTarget.firebaseId,
      } as const;

      if (isHandledDemoAccessLookupMiss(details.errorCode)) {
        logger.debug("DEMO_ACCESS_REVOKE_HANDLED_NOT_FOUND", {
          ...failureMeta,
          operation: "DEMO_ACCESS_REVOKE_HANDLED_NOT_FOUND",
        });
      } else {
        logger.error(
          "DEMO_ACCESS_REVOKE_FAIL",
          toError(revokeError) ?? new Error("demo_access_revoke_failed"),
          {
            ...failureMeta,
            operation: "DEMO_ACCESS_REVOKE_FAIL",
          },
        );
      }
      setActionError(buildDemoAccessRevokeErrorMessage(revokeError));
    }
  };

  return (
    <SettingsWorkspaceChrome
      actions={
        <div className="demo-access-route__toolbar">
          {/* Nav PRD §11: moved here from the retired top bar shortcut. */}
          <a
            className="vnext-button vnext-button--secondary"
            href={getDemoCompanyEntryUrl(window.location.origin)}
            rel="noopener noreferrer"
            target="_blank"
          >
            Open demo company
            <span className="vnext-sr-only"> (opens in a new tab)</span>
          </a>
          <VNextButton
            disabled={isFetching}
            onClick={() => {
              void handleRefresh();
            }}
            tone="secondary"
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </VNextButton>
          <VNextButton onClick={handleOpenGrantDialog} tone="primary">
            Grant demo access
          </VNextButton>
        </div>
      }
      subtitle="Grant or remove demo-company access for internal demos and training from one place."
      title="Demo access"
    >
      <div className="vnext-stack demo-access-route">
        {actionSuccess ? (
          <VNextNotice tone="info">{actionSuccess}</VNextNotice>
        ) : null}
        {actionError ? (
          <VNextNotice tone="danger">{actionError}</VNextNotice>
        ) : null}

        <div className="vnext-grid vnext-grid--two">
          <VNextCard
            subtitle="Use the smallest access change that solves the demo need, then remove it once the session is complete."
            title="Operator guidance"
          >
            <ul className="demo-access-route__guidance-list">
              <li>
                Use Firebase UID when you have it. It is the safest match for
                grants and revokes.
              </li>
              <li>
                Use email only as a lookup when you do not have the Firebase UID
                yet.
              </li>
              <li>
                Granting access to the wrong person exposes demo data and
                weakens the audit trail.
              </li>
            </ul>
          </VNextCard>

          <VNextCard title="Current access summary">
            <div className="vnext-stats-grid">
              <VNextStat
                detail="Users who currently have demo-company access enabled."
                label="Active assignments"
                value={summary.activeAssignments.toLocaleString()}
              />
              <VNextStat
                detail="Distinct admins recorded as the grant source."
                label="Unique grantors"
                value={summary.uniqueGrantors.toLocaleString()}
              />
            </div>
            <div className="demo-access-route__summary-meta">
              <VNextKeyValueList
                items={[
                  {
                    label: "Last updated",
                    value: formatVNextDateTime(summary.lastUpdatedAt),
                  },
                  {
                    label: "Assignments in scope",
                    value: assignments.length.toLocaleString(),
                  },
                ]}
              />
            </div>
          </VNextCard>
        </div>

        <DemoAccessAssignmentsCard
          assignments={visibleAssignments}
          errorMessage={assignmentErrorMessage}
          inactiveAssignmentsCount={
            assignments.length - summary.activeAssignments
          }
          isLoading={isLoading}
          isRevoking={isRevoking}
          onOpenRevokeDialog={handleOpenRevokeDialog}
          onRetry={() => {
            void handleRefresh();
          }}
          onToggleShowDisabledAssignments={() => {
            setShowDisabledAssignments((previous) => !previous);
          }}
          showDisabledAssignments={showDisabledAssignments}
        />

        <DemoAccessDialogs
          grantDialogOpen={grantDialogOpen}
          identifierType={identifierType}
          isGranting={isGranting}
          isRevoking={isRevoking}
          onCloseGrantDialog={handleCloseGrantDialog}
          onCloseRevokeDialog={handleCloseRevokeDialog}
          onConfirmGrant={() => {
            void handleGrantAccess();
          }}
          onConfirmRevoke={() => {
            void handleConfirmRevoke();
          }}
          onIdentifierTypeChange={setIdentifierType}
          onUserIdentifierChange={setUserIdentifier}
          revokeTarget={revokeTarget}
          userIdentifier={userIdentifier}
        />
      </div>
    </SettingsWorkspaceChrome>
  );
}

export default DemoAccessRoute;
